import workerpool from "workerpool";

import { ContractId } from "@abacus/ows-contract-client";
import { chunkify, Logger } from "@abacus/common";
import {
  PortHandlerResponse,
  PortReader,
  PortServices,
  withChannel,
} from "@abacus/port-utils";
import { ProgressTracker } from "@/progressTracker";

import env from "@/env";
import { Runner, JobName, ChunkRunResult } from "@/types";
import { getDupes } from "@/utils/utils";
import {
  getDefaultAbacusContractClient,
  getDefaultAbacusEventClient,
  getDefaultAbacusStateClient,
  getWorkerPool,
} from "@/utils/clientUtils";

import { sapSyncContractsJobDataSchema } from "./schemas";
import {
  SAPSyncContractsJobConfig,
  SAPSyncContractsJobData,
  SAPSyncContractsJobResult,
} from "./types";
import { LeakyBucketThrottler } from "@abacus/throttlers";

export class SAPSyncContractsJob implements Runner {
  readonly logger?: Logger;
  private pool?: workerpool.Pool;
  readonly resync: boolean;

  constructor(config?: SAPSyncContractsJobConfig) {
    this.logger = config?.logger;
    this.resync = config?.resync ?? false;
  }

  async run(data: SAPSyncContractsJobData): Promise<SAPSyncContractsJobResult> {
    try {
      const { contractIds } = sapSyncContractsJobDataSchema.parse(data);
      this.logger?.info(`Total input: ${contractIds.length}`);

      if (contractIds.length < 1) {
        return { errors: [], success: 0, total: 0 };
      }

      return await this.sapSyncContracts(contractIds);
    } finally {
      await this.terminate();
    }
  }

  private async sapSyncContracts(
    contractIds: ContractId[],
  ): Promise<SAPSyncContractsJobResult> {
    const chunkSize = env.CHUNK_SIZE;
    const errors: [number, string][] = [];
    const onBatchResult = ({
      errors: chunkErrors,
      total: chunkTotal,
    }: ChunkRunResult<void>): PortHandlerResponse<void> => {
      for (const [index, message] of chunkErrors) {
        errors.push([contractIds[index], message]);
      }
      tracker.count += chunkTotal;
      tracker.errors += chunkErrors.length;
      this.logger?.info(tracker.toString());
      return { value: undefined };
    };

    this.logger?.info(`Pinging API ows-contract-event...`);
    const contractClient = getDefaultAbacusContractClient();
    let status = (await contractClient.getHealth()).data.status;
    this.logger?.info(`\tAPI status: ${status}`);

    this.logger?.info(`Pinging API ows-event-event...`);
    const eventClient = getDefaultAbacusEventClient();
    status = (await eventClient.getHealth()).data.status;
    this.logger?.info(`\tAPI status: ${status}`);

    this.logger?.info(`Pinging API ows-state-event...`);
    const stateClient = getDefaultAbacusStateClient();
    status = (await stateClient.getHealth()).data.status;
    this.logger?.info(`\tAPI status: ${status}`);

    this.logger?.info(`Checking for duplicates...`);
    const tracker = new ProgressTracker(contractIds.length);
    const [indexes, dupeIndexes] = getDupes(contractIds);
    for (const index of dupeIndexes) {
      errors.push([index, "Skipped duplicate"]);
    }
    tracker.count = errors.length;
    tracker.errors = dupeIndexes.length;
    contractIds = indexes.map((index) => contractIds[index]);
    console.log(`Skipped duplicates: ${dupeIndexes.length}`);

    this.logger?.info(`Creating worker pool...`);
    this.pool ??= getWorkerPool(contractIds.length);
    this.logger?.info(`\tMax workers: ${this.pool.maxWorkers!}`);
    this.logger?.info(`\tChunk size: ${chunkSize}`);

    this.logger?.info(`Creating services...`);
    const throttler = new LeakyBucketThrottler({ 
      capacity: 5000, 
      leakRate: 0.8333
    });
    const chunks = chunkify(chunkSize, contractIds);
    const services = new PortServices();
    services.add("AbacusContractClient", contractClient);
    services.add("AbacusEventClient", eventClient);
    services.add("AbacusStateClient", stateClient);
    services.add("Chunks", chunks);
    services.add("Throttler", throttler);

    this.logger?.info("Running workers...");
    const workerPromises: Promise<void>[] = [];
    for (let i = 0; i < this.pool.maxWorkers!; ++i) {
      workerPromises.push(
        withChannel(({ port1, port2 }) => {
          services.listenOn(port1);
          new PortReader(port1).on("results", onBatchResult);
          return this.pool!.exec(
            JobName.SapSyncContracts,
            [port2, this.resync],
            {
              transfer: [port2],
            },
          );
        }),
      );
    }

    await Promise.all(workerPromises);

    return {
      errors,
      success: contractIds.length - errors.length,
      total: contractIds.length,
    };
  }

  private async terminate(): Promise<void> {
    const results = await Promise.allSettled([this.pool?.terminate()]);
    this.pool = undefined;
    for (const result of results) {
      if (result.status === "rejected") {
        throw result.reason;
      }
    }
  }

  async [Symbol.asyncDispose](): Promise<void> {
    this.terminate();
  }
}
